import routes from "../../../../Config/routes";

/**
 * Extracts base slugs from routes object to prevent conflicts with bio pages
 * This automatically updates when routes.js changes
 */
function extractProhibitedSlugs() {
  const prohibited = new Set();
  
  // Helper to extract the first segment of a route
  const extractFirstSegment = (path) => {
    if (!path || typeof path !== 'string') return null;
    
    // Remove leading slash
    const cleaned = path.startsWith('/') ? path.slice(1) : path;
    
    // Get first segment (before any / or :)
    const firstSegment = cleaned.split('/')[0].split(':')[0];
    
    // Only add if it's not empty and not a parameter
    if (firstSegment && !firstSegment.startsWith(':')) {
      return firstSegment.toLowerCase();
    }
    
    return null;
  };
  
  // Extract from all routes
  Object.values(routes).forEach(route => {
    const slug = extractFirstSegment(route);
    if (slug) {
      prohibited.add(slug);
    }
  });
  
  // Add additional reserved words that should always be protected
  const alwaysReserved = [
    // System routes
    'api', 'admin', 'system', 'config', 'settings',
    // Common subdomains/paths
    'www', 'mail', 'ftp', 'blog', 'shop', 'store',
    // Authentication
    'auth', 'oauth', 'signin', 'signup', 'signout',
    // Common pages
    'index', 'home', 'about', 'contact', 'help',
    // Technical
    'static', 'assets', 'public', 'private',
    // Blitz specific
    'blitz', 'blitzpay', 'creator', 'agency', 'user',
    // Legal
    'terms', 'privacy', 'legal', 'dmca',
    // Special characters that could cause issues
    'null', 'undefined', 'true', 'false',
  ];
  
  alwaysReserved.forEach(word => prohibited.add(word));
  
  return Array.from(prohibited).sort();
}

// Generate the list once
const PROHIBITED_SLUGS = extractProhibitedSlugs();

/**
 * Check if a slug is prohibited
 * @param {string} slug - The slug to validate
 * @returns {boolean} - True if prohibited
 */
export function isProhibitedSlug(slug) {
  if (!slug || typeof slug !== 'string') return true;
  
  const normalized = slug.toLowerCase().trim();
  
  // Check exact match
  if (PROHIBITED_SLUGS.includes(normalized)) {
    return true;
  }
  
  // Check if it starts with a prohibited prefix (e.g., "api-" or "admin_")
  const hasProhibitedPrefix = PROHIBITED_SLUGS.some(prohibited => 
    normalized.startsWith(prohibited + '-') || 
    normalized.startsWith(prohibited + '_')
  );
  
  return hasProhibitedPrefix;
}

/**
 * Get validation result with helpful message
 * @param {string} slug - The slug to validate
 * @returns {object} - { isValid, message }
 */
export function validateSlug(slug) {
  if (!slug || typeof slug !== 'string' || slug.trim() === '') {
    return {
      isValid: false,
      message: 'Slug is required'
    };
  }
  
  const normalized = slug.toLowerCase().trim();
  
  // Check length
  if (normalized.length < 3) {
    return {
      isValid: false,
      message: 'Slug must be at least 3 characters long'
    };
  }
  
  if (normalized.length > 50) {
    return {
      isValid: false,
      message: 'Slug must be less than 50 characters'
    };
  }
  
  // Check format (alphanumeric, hyphens, underscores only)
  const validFormat = /^[a-z0-9_-]+$/.test(normalized);
  if (!validFormat) {
    return {
      isValid: false,
      message: 'Slug can only contain lowercase letters, numbers, hyphens, and underscores'
    };
  }
  
  // Check if it starts or ends with special characters
  if (normalized.startsWith('-') || normalized.startsWith('_') || 
      normalized.endsWith('-') || normalized.endsWith('_')) {
    return {
      isValid: false,
      message: 'Slug cannot start or end with hyphens or underscores'
    };
  }
  
  // Check prohibited list
  if (isProhibitedSlug(normalized)) {
    return {
      isValid: false,
      message: `"${normalized}" is a reserved route and cannot be used. Try adding a prefix or suffix!`
    };
  }
  
  return {
    isValid: true,
    message: 'Slug is available!'
  };
}

/**
 * Get all prohibited slugs for display
 * @returns {string[]} - Sorted array of prohibited slugs
 */
export function getProhibitedSlugs() {
  return [...PROHIBITED_SLUGS];
}

/**
 * Suggest alternative slugs based on a prohibited one
 * @param {string} slug - The prohibited slug
 * @returns {string[]} - Array of suggested alternatives
 */
export function suggestAlternatives(slug) {
  if (!slug) return [];
  
  const normalized = slug.toLowerCase().trim();
  
  return [
    `${normalized}-page`,
    `${normalized}-bio`,
    `my-${normalized}`,
    `${normalized}-official`,
    `get-${normalized}`,
  ];
}

export default PROHIBITED_SLUGS;